import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
} from '@mui/material';
import { getStoresByInstitutionId } from '../../services/store.service';
import NotInChargeNotice from '../store/NotInChargeNotice';
import { Store } from '../../interfaces/store.interface';
import { StocktakeService } from '../../services/stocktake.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import { helpWhy } from '../../help/why';

const StocktakeCreate: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [stores, setStores] = useState<Store[]>([]);
    const [storesLoaded, setStoresLoaded] = useState(false);
    const [selectedStoreId, setSelectedStoreId] = useState<string>('');
    const [stocktakeDate, setStocktakeDate] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => {
        const fetchStores = async () => {
            try {
                const response = await getStoresByInstitutionId(0, 100);
                setStores(response.content);
                setStoresLoaded(true);
            } catch (err) {
                console.error('Error fetching stores:', err);
                toast.showApiError(err, 'Failed to load stores');
            }
        };
        fetchStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStoreChange = (id: string) => {
        setSelectedStoreId(id);
    };

    const handleSubmit = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) {
            toast.showError('No institution selected');
            return;
        }
        if (!selectedStoreId) {
            toast.showError('Please select a store');
            return;
        }
        try {
            setSubmitting(true);
            const created = await StocktakeService.createStocktake({
                storeId: selectedStoreId,
                institutionId,
                stocktakeDate: stocktakeDate || undefined,
                notes: notes.trim() || undefined,
            });
            toast.showSuccess('Stocktake created');
            navigate(`/stocktakes/${created.id}`);
        } catch (err) {
            console.error('Error creating stocktake:', err);
            toast.showApiError(err, 'Failed to create stocktake');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="store.countForm.page">
            <Typography variant="h4" sx={{ mb: 3 }}>
                New Stocktake
            </Typography>
            {storesLoaded && stores.length === 0 && <NotInChargeNotice />}
            <Paper elevation={3} sx={{ p: 3, maxWidth: 520 }} data-help-section="store.countForm.form">
                <SearchableSelect<Store>
                    options={stores}
                    value={selectedStoreId}
                    onChange={handleStoreChange}
                    getOptionLabel={store => store.storeName}
                    data-help="store.countForm.field.store" label="Select Store"
                    sx={{ mb: 3 }}
                />

                <TextField
                    data-help="store.countForm.field.date" label="Stocktake Date"
                    type="date"
                    fullWidth
                    value={stocktakeDate}
                    onChange={(e) => setStocktakeDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 3 }}
                />

                <TextField
                    data-help="store.countForm.field.notes" label="Notes"
                    fullWidth
                    multiline
                    minRows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    sx={{ mb: 3 }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        data-help="store.countForm.create"
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting || !selectedStoreId}
                        {...helpWhy(!submitting && !selectedStoreId ? { kind: 'status', message: 'Choose a store first.' } : null)}
                    >
                        {submitting ? 'Creating...' : 'Create Stocktake'}
                    </Button>
                    <Button data-help="store.countForm.cancel" variant="outlined" onClick={() => navigate('/stocktakes')}>
                        Cancel
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default StocktakeCreate;
